/**
 * @fileoverview Hook for Phase 1 (Critical) bootstrap data fetching.
 *
 * This hook handles the critical bootstrap fetch which blocks initial UI render.
 * Critical data includes session state, images, settings, and other data required
 * to display the main application interface.
 *
 * ## Design Principles
 *
 * 1. **Idempotent Execution**: Multiple calls will not trigger duplicate fetches.
 *    Uses a completion ref to track if the fetch has already completed.
 *
 * 2. **React Strict Mode Safe**: Uses useRef to track completion state across
 *    the simulated unmount/remount cycle in development mode.
 *
 * 3. **Proper Abort Handling**: Respects the provided AbortSignal for cleanup
 *    and does not set error state on clean unmounts.
 *
 * 4. **401 Handling**: Does not set error state for 401 errors since api.ts
 *    handles logout automatically.
 *
 * 5. **Navigation Guard**: Only navigates to activeMainTab when the current
 *    path is root ('/'), preventing unintended redirects when users bookmark
 *    or directly access specific routes.
 *
 * ## Usage
 *
 * ```tsx
 * function BootstrapHandler() {
 *   const { isAuthenticated } = useAuth();
 *   const abortControllerRef = useRef<AbortController>(new AbortController());
 *
 *   const { isLoading, isComplete, error } = useCriticalBootstrap(
 *     isAuthenticated,
 *     abortControllerRef.current.signal
 *   );
 *
 *   useEffect(() => {
 *     return () => abortControllerRef.current.abort();
 *   }, []);
 *
 *   if (error) return <ErrorScreen error={error} />;
 *   if (isLoading) return <LoadingScreen />;
 *   if (!isComplete) return <LoadingScreen />;
 *
 *   return <MainApp />;
 * }
 * ```
 *
 * @see react-ui/src/hooks/bootstrap/types.ts for CriticalBootstrapResponse
 * @see react-ui/src/hooks/bootstrap/useFetchWithRetry.ts for fetch utilities
 * @see docs/adr/0002-bootstrap-state-hydration.md for architecture decisions
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useGlobalState } from '../../state/useGlobalState';
import { useImageLibraryStore } from '../../state/createImageLibraryStore';
import { ApiError } from '../../services/apiErrors';
import { logger } from '../../services/logger.client';
import { fetchWithRetry } from './useFetchWithRetry';
import { useBootstrapContext } from './BootstrapProvider';
import type { CriticalBootstrapResponse } from './types';
import type { Image, StoreState } from '../../types/types';

// =============================================================================
// Types
// =============================================================================

/**
 * Result type for the useCriticalBootstrap hook.
 *
 * Provides the loading state, completion status, and any error that occurred
 * during the critical bootstrap fetch.
 */
export interface CriticalBootstrapHookResult {
  /**
   * True while the critical bootstrap fetch is in progress.
   * When true, the main application UI should not render - show a loading screen.
   */
  isLoading: boolean;

  /**
   * True when the critical bootstrap has completed successfully.
   * When false and isLoading is false, either the user is not authenticated
   * or an error occurred.
   */
  isComplete: boolean;

  /**
   * The error object if a fatal error occurred during critical bootstrap.
   * Will be null if no error occurred.
   * May be an instance of BootstrapTimeoutError, RetryExhaustedError, ApiError, etc.
   *
   * Note: 401 errors and AbortErrors do NOT populate this field since they are
   * handled specially (logout and clean unmount, respectively).
   */
  error: Error | null;
}

// =============================================================================
// Module-level state for React Strict Mode
// =============================================================================
// In React 18+ Strict Mode, components are mounted, unmounted, and remounted
// during development. We use module-level state to ensure completion persists
// across these cycles and to prevent duplicate fetches.

/**
 * Module-level Promise for the in-progress critical bootstrap.
 * When a bootstrap is in progress, this holds a Promise that resolves when complete.
 * Concurrent callers can await this Promise instead of starting duplicate fetches.
 * The Promise resolves to `true` on success, `false` on abort/failure.
 */
let criticalBootstrapPromise: Promise<boolean> | null = null;

/**
 * Module-level flag to track if critical bootstrap has completed successfully.
 * This persists across component unmount/remount cycles.
 */
let hasCriticalBootstrapCompleted = false;

/**
 * Module-level storage for the last bootstrap error.
 * This persists across component unmount/remount cycles, ensuring errors
 * are displayed even if the component unmounts during the retry period.
 */
let lastBootstrapError: Error | null = null;

/**
 * Debug helper to log module-level error state changes.
 *
 * @param error - The new error value (or null)
 * @param source - Description of where the change originated
 */
function setLastBootstrapError(error: Error | null, source: string): void {
  const prevError = lastBootstrapError;
  lastBootstrapError = error;
  logger.debug(
    {
      source,
      prevError: prevError ? { name: prevError.name, message: prevError.message } : null,
      newError: error ? { name: error.name, message: error.message } : null,
      changed: prevError !== error,
    },
    'useCriticalBootstrap: Module-level lastBootstrapError changed'
  );
}

/**
 * Resets the module-level bootstrap state.
 *
 * This is useful for:
 * - Testing (reset state between tests)
 * - Explicit logout scenarios
 * - Force retry after persistent errors
 *
 * @example
 * ```tsx
 * // Reset before running tests
 * beforeEach(() => {
 *   resetCriticalBootstrapState();
 * });
 * ```
 */
export function resetCriticalBootstrapState(): void {
  logger.info(
    {
      hadPromise: criticalBootstrapPromise !== null,
      hadCompleted: hasCriticalBootstrapCompleted,
      hadError: lastBootstrapError !== null,
    },
    'useCriticalBootstrap: resetCriticalBootstrapState called - clearing all module-level state'
  );
  criticalBootstrapPromise = null;
  hasCriticalBootstrapCompleted = false;
  setLastBootstrapError(null, 'resetCriticalBootstrapState');
}

// =============================================================================
// Hook
// =============================================================================

/**
 * Hook for Phase 1 (Critical) bootstrap data fetching.
 *
 * Fetches critical session data from `/api/bootstrap/critical` and hydrates
 * both the main Zustand store and the image library store. On successful
 * completion, navigates to the user's last active tab (if on root path).
 *
 * ## Parameters
 *
 * @param isAuthenticated - Whether the user is currently authenticated.
 *   When false, the hook returns immediately without fetching (isComplete=false).
 *
 * @param signal - AbortSignal for cancellation. Pass this from an AbortController
 *   that is aborted on component unmount to ensure proper cleanup.
 *
 * ## Return Value
 *
 * Returns a `CriticalBootstrapHookResult` object with:
 * - `isLoading`: True while fetch is in progress
 * - `isComplete`: True when fetch completed successfully
 * - `error`: Error object if a fatal error occurred, null otherwise
 *
 * ## Idempotency
 *
 * This hook is idempotent - calling it multiple times (e.g., due to React Strict
 * Mode double-rendering) will not trigger duplicate fetches. The hook tracks
 * completion at the module level and returns cached results on subsequent calls.
 *
 * ## Error Handling
 *
 * - 401 errors: NOT set as error state. The api.ts layer handles logout.
 * - AbortError: NOT set as error state. Clean unmount, no action needed.
 * - All other errors: Set as error state for display to user.
 *
 * ## Navigation
 *
 * On successful completion, if the current path is '/' (root), the hook navigates
 * to the user's saved `activeMainTab`. This preserves the user's last position
 * while avoiding unintended redirects when directly accessing a specific route.
 *
 * @example
 * ```tsx
 * function BootstrapHandler() {
 *   const { isAuthenticated } = useAuth();
 *   const { createAbortController, abortController } = useBootstrapContext();
 *
 *   useEffect(() => {
 *     if (isAuthenticated && !abortController) {
 *       createAbortController();
 *     }
 *   }, [isAuthenticated, abortController, createAbortController]);
 *
 *   const { isLoading, isComplete, error } = useCriticalBootstrap(
 *     isAuthenticated,
 *     abortController?.signal ?? new AbortController().signal
 *   );
 *
 *   if (error) {
 *     return <ErrorDisplay error={error} onRetry={() => window.location.reload()} />;
 *   }
 *
 *   if (isLoading || !isComplete) {
 *     return <LoadingSpinner />;
 *   }
 *
 *   return <MainApplication />;
 * }
 * ```
 */
export function useCriticalBootstrap(
  isAuthenticated: boolean,
  signal: AbortSignal
): CriticalBootstrapHookResult {
  const navigate = useNavigate();
  const location = useLocation();

  // Get dispatch from bootstrap context to notify when critical bootstrap completes
  // This is critical for useCombatSync which checks hasCriticalCompleted before syncing
  const { dispatch } = useBootstrapContext();

  // Component-level state
  const [isLoading, setIsLoading] = useState<boolean>(!hasCriticalBootstrapCompleted && isAuthenticated);
  const [error, setError] = useState<Error | null>(lastBootstrapError);

  // Track if this effect instance is still mounted
  const isMountedRef = useRef<boolean>(true);

  // Track if we've completed in this hook instance (for the return value)
  const isCompleteRef = useRef<boolean>(hasCriticalBootstrapCompleted);

  // Debug: Log state on each render
  logger.debug(
    {
      isAuthenticated,
      isLoading,
      isComplete: isCompleteRef.current,
      error: error ? { name: error.name, message: error.message } : null,
      moduleState: {
        hasCriticalBootstrapCompleted,
        hasPromise: criticalBootstrapPromise !== null,
        hasLastError: lastBootstrapError !== null,
      },
    },
    'useCriticalBootstrap: Hook render - current state snapshot'
  );

  /**
   * Performs the critical bootstrap fetch and state hydration.
   *
   * This function:
   * 1. Fetches /api/bootstrap/critical with retry logic
   * 2. Hydrates the image library store
   * 3. Hydrates the main global state store
   * 4. Navigates to activeMainTab if on root path
   * 5. Updates completion state
   *
   * @returns Promise<boolean> - True on success, false on abort/failure
   */
  const performCriticalBootstrap = useCallback(async (): Promise<boolean> => {
    try {
      logger.info('useCriticalBootstrap: Fetching critical bootstrap state...');

      const criticalState = await fetchWithRetry<CriticalBootstrapResponse>(
        '/api/bootstrap/critical',
        signal,
        'critical bootstrap'
      );

      // Check if we were aborted while processing
      if (signal.aborted) {
        logger.info('useCriticalBootstrap: Aborted after critical fetch, discarding results.');
        return false;
      }

      // Extract and apply the critical bootstrap data
      const { images, session, settings } = criticalState;

      logger.info(
        { imageCount: Array.isArray(images) ? images.length : 0 },
        'useCriticalBootstrap: Images received from API.'
      );

      // Hydrate the image library store
      // Type assertion is safe here because the actual validation happens in hydrateState via Zod
      const typedImages = Array.isArray(images) ? (images as Image[]) : [];
      useImageLibraryStore.getState().setImages(typedImages);

      // Hydrate the main session state
      // The hydrateState function validates the data via Zod schemas before applying
      // Type assertions are safe here as runtime validation catches any mismatches
      useGlobalState.getState().hydrateState({
        ...session,
        settings: settings as StoreState['settings'],
        images: typedImages,
      });
      logger.info('useCriticalBootstrap: Critical state successfully hydrated.');

      // Mark critical bootstrap as completed (module-level)
      logger.info(
        { prevCompleted: hasCriticalBootstrapCompleted },
        'useCriticalBootstrap: Marking critical bootstrap as completed'
      );
      hasCriticalBootstrapCompleted = true;
      isCompleteRef.current = true;
      setLastBootstrapError(null, 'criticalBootstrapSuccess');

      // Dispatch CRITICAL_SUCCESS to the bootstrap context
      // This is essential for useCombatSync which checks hasCriticalCompleted
      // before syncing combat state to prevent overwriting server state during hydration.
      // Without this dispatch, the context's hasCriticalCompleted would remain false
      // and combat sync would never trigger.
      dispatch({
        type: 'CRITICAL_SUCCESS',
        payload: criticalState,
      });
      logger.info('useCriticalBootstrap: Dispatched CRITICAL_SUCCESS to bootstrap context');

      // After state is hydrated, navigate to the user's last active main tab.
      // Only do this if the user is on the root path ('/') - not if they navigated
      // directly to a specific route like '/world-building' or '/data'.
      // This prevents unintended redirects when users bookmark or directly access routes.
      if (isMountedRef.current) {
        const currentPath = location.pathname;
        const savedTab = useGlobalState.getState().settings.activeMainTab;

        // Only redirect if we're at root and the saved tab is different
        if (savedTab && currentPath === '/' && savedTab !== '/') {
          logger.info(
            { savedTab, currentPath },
            'useCriticalBootstrap: Navigating to saved activeMainTab'
          );
          navigate(savedTab);
        }
      }

      // Critical loading is complete - UI can now render
      logger.info(
        { isMounted: isMountedRef.current },
        'useCriticalBootstrap: Critical bootstrap complete'
      );

      if (isMountedRef.current) {
        setIsLoading(false);
        setError(null);
      }

      return true;
    } catch (err: unknown) {
      // Ignore abort errors (component unmount or intentional cancellation)
      if (err instanceof DOMException && err.name === 'AbortError') {
        logger.info('useCriticalBootstrap: Critical bootstrap was aborted (likely unmount). Ignoring.');
        return false;
      }

      // The api wrapper handles 401s by logging the user out.
      // We only need to set a fatal error state for other types of errors.
      if (err instanceof ApiError && err.status === 401) {
        logger.warn(
          { status: 401 },
          'useCriticalBootstrap: Caught a 401 error. User has been logged out. Ignoring error.'
        );
        setLastBootstrapError(null, 'caught401Error');
      } else {
        logger.error(
          {
            err,
            errorName: err instanceof Error ? err.name : 'Unknown',
            errorMessage: err instanceof Error ? err.message : String(err),
            isMounted: isMountedRef.current,
          },
          'useCriticalBootstrap: A fatal error occurred during critical bootstrap.'
        );

        // Store error at module level so it persists across component unmount/remount
        const errorToStore = err instanceof Error ? err : new Error('An unknown error occurred during initialization.');
        setLastBootstrapError(errorToStore, 'criticalBootstrapError');

        // Dispatch CRITICAL_ERROR to the bootstrap context for state machine tracking
        dispatch({ type: 'CRITICAL_ERROR', payload: errorToStore });
        logger.debug('useCriticalBootstrap: Dispatched CRITICAL_ERROR to bootstrap context');

        // Also set component-level error state if still mounted
        if (isMountedRef.current) {
          logger.info(
            { settingError: errorToStore.message },
            'useCriticalBootstrap: Setting component-level error state'
          );
          setError(errorToStore);
        }
      }

      // Still need to clear loading state on error
      logger.info(
        { isMounted: isMountedRef.current },
        'useCriticalBootstrap: Clearing loading state after error'
      );
      if (isMountedRef.current) {
        setIsLoading(false);
      }

      return false;
    }
  }, [signal, navigate, location.pathname, dispatch]);

  useEffect(() => {
    logger.info('useCriticalBootstrap: useEffect triggered.');
    isMountedRef.current = true;

    // Track if we should skip initialization due to persisted error
    let skipInitialization = false;

    // Check for persisted bootstrap error from previous mount.
    // This can happen in React Strict Mode when:
    // 1. Mount 1: Bootstrap fails, error is stored in lastBootstrapError
    // 2. Unmount 1: Cleanup runs
    // 3. Mount 2: This check finds the persisted error
    // In this case, we should show the error UI immediately without retrying.
    if (lastBootstrapError) {
      logger.warn(
        {
          err: lastBootstrapError,
          errorName: lastBootstrapError.name,
          errorMessage: lastBootstrapError.message,
        },
        'useCriticalBootstrap: Found persisted bootstrap error from previous attempt. Showing error immediately.'
      );
      setError(lastBootstrapError);
      setIsLoading(false);
      skipInitialization = true;
    }

    const initialize = async (): Promise<void> => {
      logger.info({ isAuthenticated }, 'useCriticalBootstrap: Starting initialize function.');

      // If the user is not authenticated, we don't need to fetch app state.
      if (!isAuthenticated) {
        logger.info('useCriticalBootstrap: Not authenticated, skipping state fetch.');
        // Clear any bootstrap errors when logging out
        setLastBootstrapError(null, 'notAuthenticated');
        if (isMountedRef.current) {
          setIsLoading(false);
        }
        return;
      }

      // If critical bootstrap already completed successfully, don't refetch
      if (hasCriticalBootstrapCompleted) {
        logger.info('useCriticalBootstrap: Critical bootstrap already completed. Skipping fetch.');
        isCompleteRef.current = true;
        if (isMountedRef.current) {
          setIsLoading(false);
        }
        return;
      }

      // Promise-based guard: If a bootstrap is already in progress, await it instead
      // of starting a duplicate fetch. This solves the race condition where the second
      // React Strict Mode render would see a flag as true but the first request was
      // aborted, leaving isLoading stuck as true forever.
      if (criticalBootstrapPromise !== null) {
        logger.info(
          { promiseExists: true },
          'useCriticalBootstrap: Critical bootstrap already in progress. Awaiting existing promise.'
        );
        const success = await criticalBootstrapPromise;
        logger.info(
          { promiseResolved: true, success, isMounted: isMountedRef.current },
          'useCriticalBootstrap: Awaited critical bootstrap promise resolved'
        );

        if (success) {
          // Bootstrap completed successfully, set loading to false
          isCompleteRef.current = true;
          if (isMountedRef.current) {
            setIsLoading(false);
          }
          return;
        }

        // Bootstrap failed or was aborted. Check if there's a persisted error to show.
        if (lastBootstrapError) {
          logger.warn(
            {
              err: lastBootstrapError,
              errorName: lastBootstrapError.name,
              errorMessage: lastBootstrapError.message,
              isMounted: isMountedRef.current,
            },
            'useCriticalBootstrap: Awaited promise failed with persisted error.'
          );
          if (isMountedRef.current) {
            setError(lastBootstrapError);
            setIsLoading(false);
          }
          return;
        }

        // Bootstrap was aborted (e.g., React Strict Mode unmount) but no error.
        // Continue to start a new bootstrap attempt below.
        logger.info(
          { noPersistedError: true },
          'useCriticalBootstrap: Awaited promise was aborted without error. Starting new bootstrap attempt.'
        );
      }

      // Ensure loading state is true when starting a new bootstrap fetch.
      logger.info(
        { isMounted: isMountedRef.current, settingIsLoading: true },
        'useCriticalBootstrap: Starting new critical bootstrap - setting loading to true'
      );
      if (isMountedRef.current) {
        setIsLoading(true);
      }

      // Dispatch START_CRITICAL to the bootstrap context to track state machine transitions
      dispatch({ type: 'START_CRITICAL' });
      logger.debug('useCriticalBootstrap: Dispatched START_CRITICAL to bootstrap context');

      // Create the promise that will be shared with concurrent callers.
      // This promise resolves to true on success, false on abort/failure.
      criticalBootstrapPromise = (async (): Promise<boolean> => {
        try {
          return await performCriticalBootstrap();
        } finally {
          // Clean up module-level state for critical fetch
          criticalBootstrapPromise = null;
          logger.info('useCriticalBootstrap: Critical fetch finally block reached.');
        }
      })();

      // Await the promise we just created
      await criticalBootstrapPromise;
    };

    // Only run initialization if we don't have a persisted error.
    // This prevents overwriting the error state with isLoading=true.
    logger.info(
      { skipInitialization, willRunInitialize: !skipInitialization },
      'useCriticalBootstrap: useEffect decision - whether to run initialize()'
    );
    if (!skipInitialization) {
      void initialize();
    }

    // Cleanup function: mark as unmounted
    // Note: We don't abort here because the signal is owned by the caller
    return () => {
      isMountedRef.current = false;
      logger.info('useCriticalBootstrap: Cleanup - component unmounting.');
    };
  }, [isAuthenticated, performCriticalBootstrap, dispatch]);

  // Debug: Log the return value
  logger.debug(
    {
      returning: { isLoading, isComplete: isCompleteRef.current, hasError: error !== null },
    },
    'useCriticalBootstrap: Returning state from hook'
  );

  return {
    isLoading,
    isComplete: isCompleteRef.current,
    error,
  };
}
